import { tracked } from "@glimmer/tracking";
import { get } from "@ember/object";
import { service } from "@ember/service";
import { dasherize } from "@ember/string";
import { trustHTML } from "@ember/template";
import { decorateUsername } from "discourse/helpers/decorate-username-selector";
import noop from "discourse/helpers/noop";
import { avatarUrl } from "discourse/lib/avatar-utils";
import { bind } from "discourse/lib/decorators";
import { helperContext } from "discourse/lib/helpers";
import { withPluginApi } from "discourse/lib/plugin-api";
import { emojiUnescape } from "discourse/lib/text";
import { escapeExpression } from "discourse/lib/utilities";
import { i18n } from "discourse-i18n";
import ChatChannelListSidebarMenu from "discourse/plugins/chat/discourse/components/chat-channel-list-sidebar-menu";
import ChatChannelSidebarContextMenu from "discourse/plugins/chat/discourse/components/chat-channel-sidebar-context-menu";
import ChatSidebarIndicators from "discourse/plugins/chat/discourse/components/chat-sidebar-indicators";
import { CHANNEL_LIST_SECTION_OPTIONS } from "discourse/plugins/chat/discourse/lib/chat-channel-list-options";
import {
  CHAT_PANEL,
  initSidebarState,
} from "discourse/plugins/chat/discourse/lib/init-sidebar-state";

const CHAT_STARRED_CHANNELS_SECTION = "chat-starred-channels";

function createChannelLink(BaseCustomSidebarSectionLink, options = {}) {
  const { showSuffix = true } = options;

  return class extends BaseCustomSidebarSectionLink {
    route = "chat.channel";
    suffixType = "icon";
    hoverType = "icon";

    constructor({
      channel,
      chatService,
      currentUser,
      siteSettings,
      chatStateManager,
    }) {
      super(...arguments);
      this.channel = channel;
      this.chatService = chatService;
      this.siteSettings = siteSettings;
      this.currentUser = currentUser;
      this.chatStateManager = chatStateManager;

      if (this.isOneOnOneDM) {
        const user = this.channel.chatable.users?.[0];
        if (user?.username !== i18n("chat.deleted_chat_username")) {
          user.statusManager.trackStatus();
        }
      }
    }

    @bind
    willDestroy() {
      if (this.isOneOnOneDM) {
        this.channel.chatable.users?.[0]?.statusManager?.stopTrackingStatus();
      }
    }

    get isDM() {
      return this.channel.isDirectMessageChannel;
    }

    get isOneOnOneDM() {
      return this.isDM && this.channel.chatable.users.length === 1;
    }

    get name() {
      return this.isDM
        ? this.channel.slugifiedTitle
        : dasherize(this.channel.slugifiedTitle);
    }

    get classNames() {
      const classes = [];

      if (this.channel.currentUserMembership?.muted) {
        classes.push("sidebar-section-link--muted");
      }

      if (
        this.channel.id === this.chatService.activeChannel?.id &&
        (this.chatStateManager.isDrawerExpanded ||
          this.chatStateManager.isFullPageActive)
      ) {
        classes.push("sidebar-section-link--active");
      }

      classes.push(`channel-${this.channel.id}`);

      return classes.join(" ");
    }

    get models() {
      return this.channel.routeModels;
    }

    get text() {
      if (this.isDM) {
        if (this.channel.chatable.group) {
          return this.channel.displayTitle;
        } else {
          const username = this.channel.escapedTitle.replaceAll("@", "");
          return trustHTML(
            `${escapeExpression(username)}${decorateUsername(
              escapeExpression(username)
            )}`
          );
        }
      } else {
        return trustHTML(emojiUnescape(this.channel.escapedTitle));
      }
    }

    get title() {
      if (this.isDM) {
        if (this.channel.chatable.group) {
          return i18n("chat.placeholder_channel", {
            channelName: this.channel.escapedTitle,
          });
        } else {
          return i18n("chat.placeholder_users", {
            commaSeparatedNames: this.channel.escapedTitle,
          });
        }
      } else {
        return this.channel.escapedDescription
          ? trustHTML(this.channel.escapedDescription)
          : `${this.channel.escapedTitle} ${i18n("chat.title")}`;
      }
    }

    get prefixType() {
      if (this.isDM) {
        if (displayedEmoji(this.channel)) {
          return "emoji";
        } else if (this.channel.chatable.group) {
          if (this.channel.chatable.users.length === 1) {
            return "image";
          } else {
            return "text";
          }
        } else {
          return "image";
        }
      } else {
        return displayedEmoji(this.channel) ? "emoji" : "icon";
      }
    }

    get prefixValue() {
      if (this.isDM) {
        if (displayedEmoji(this.channel)) {
          return displayedEmoji(this.channel);
        } else if (this.channel.chatable.group) {
          if (this.channel.chatable.users.length === 1) {
            return avatarUrl(
              this.channel.chatable.users[0].avatar_template,
              "tiny"
            );
          } else {
            return this.channel.membershipsCount;
          }
        } else {
          return avatarUrl(
            this.channel.chatable.users[0].avatar_template,
            "tiny"
          );
        }
      } else {
        return displayedEmoji(this.channel) ?? "d-chat";
      }
    }

    get prefixColor() {
      return this.isDM ? null : this.channel.chatable.color;
    }

    get prefixBadge() {
      return !this.isDM && this.channel.chatable.read_restricted ? "lock" : "";
    }

    get prefixCSSClass() {
      if (this.isDM) {
        const activeUsers = this.chatService.presenceChannel.users;
        const user = this.channel.chatable.users[0];

        if (
          !!activeUsers?.find((item) => get(item, "id") === user?.id) ||
          !!activeUsers?.find(
            (item) => get(item, "username") === user?.username
          )
        ) {
          return "active";
        }
      }
      return "";
    }

    get suffixComponent() {
      return ChatSidebarIndicators;
    }

    get suffixArgs() {
      if (this.isDM) {
        return {
          userStatus: this.isOneOnOneDM
            ? this.channel.chatable.users[0].get("status")
            : null,
          unreadCount: this.channel.tracking.unreadCount,
          unreadThreadsCount: this.channel.unreadThreadsCountSinceLastViewed,
          mentionCount: this.channel.tracking.mentionCount,
          watchedThreadsUnreadCount:
            this.channel.tracking.watchedThreadsUnreadCount,
          isDirectMessageChannel: true,
        };
      } else {
        return {
          unreadCount: this.channel.tracking.unreadCount,
          unreadThreadsCount:
            this.chatService.activeChannel?.id !== this.channel.id
              ? this.channel.unreadThreadsCountSinceLastViewed
              : 0,
          mentionCount: this.channel.tracking.mentionCount,
          watchedThreadsUnreadCount:
            this.channel.tracking.watchedThreadsUnreadCount,
          isDirectMessageChannel: false,
        };
      }
    }

    get suffixValue() {
      return showSuffix ? "" : "";
    }

    get suffixCSSClass() {
      return showSuffix ? "" : "";
    }
  };
}

function channelListActions(section, menuService, preferences, links) {
  const actions = [];
  const bypassed = preferences.isFilterBypassedFor(section);
  if (bypassed || (!preferences.isDefaultFilterFor(section) && !links.length)) {
    actions.push({
      id: "toggleChannelFilter",
      icon: bypassed ? "filter" : "filter-circle-xmark",
      title: i18n(
        bypassed
          ? "chat.channel_list.apply_filters"
          : "chat.channel_list.empty.show_all"
      ),
      disabled: preferences.isSavingFilterFor(section),
      action: () => preferences.toggleFilter(section),
    });
  }
  actions.push(channelListOptionsAction(section, menuService));
  return actions;
}

function channelListOptionsAction(section, menuService) {
  return {
    id: "channelListOptions",
    title: i18n("chat.channel_list.options.title"),
    action: (event) => {
      menuService.show(event.currentTarget, {
        component: ChatChannelListSidebarMenu,
        contentRole: "menu",
        identifier: "chat-channel-list-options-menu",
        modalForMobile: true,
        placement: "right-start",
        data: { section, ...CHANNEL_LIST_SECTION_OPTIONS[section] },
      });
    },
  };
}

// Emoji are skipped while disabled so the prefix falls back to the channel's
// icon, avatar, or member count instead of rendering the raw `:code:`.
function displayedEmoji(channel) {
  return helperContext().siteSettings.enable_emoji ? channel.emoji : null;
}

export default {
  name: "chat-sidebar",
  initialize(container) {
    this.chatService = container.lookup("service:chat");
    this.siteSettings = container.lookup("service:site-settings");
    this.currentUser = container.lookup("service:current-user");

    const canViewPublicChatAnonymously =
      this.chatService.anonymousUserCanViewPublicChat;

    if (!this.chatService.userCanChat && !canViewPublicChatAnonymously) {
      return;
    }

    withPluginApi((api) => {
      const chatStateManager = container.lookup("service:chat-state-manager");

      api.addSidebarPanel(
        (BaseCustomSidebarPanel) =>
          class ChatSidebarPanel extends BaseCustomSidebarPanel {
            key = CHAT_PANEL;
            switchButtonLabel = i18n("sidebar.panels.chat.label");
            switchButtonIcon = "d-chat";
            scrollActiveLinkIntoView = true;

            get switchButtonDefaultUrl() {
              return chatStateManager.lastKnownChatURL || "/chat";
            }
          }
      );

      if (canViewPublicChatAnonymously) {
        api.setCombinedSidebarMode();
        api.showSidebarSwitchPanelButtons();
        this.chatService.loadChannels();
      } else {
        initSidebarState(api, api.getCurrentUser());
      }
    });

    withPluginApi((api) => {
      const chatChannelsManager = container.lookup(
        "service:chat-channels-manager"
      );
      const chatStateManager = container.lookup("service:chat-state-manager");

      if (this.siteSettings.chat_search_enabled && this.currentUser) {
        api.addSidebarSection(
          (BaseCustomSidebarSection, BaseCustomSidebarSectionLink) => {
            const SidebarChatSearchSectionLink = class extends BaseCustomSidebarSectionLink {
              route = "chat.search";
              text = i18n("chat.search.title");
              title = i18n("chat.search.title");
              name = "chat-search";
              prefixType = "icon";
              prefixValue = "magnifying-glass";
            };

            const SidebarChatSearchSection = class extends BaseCustomSidebarSection {
              hideSectionHeader = true;
              name = "chat-search";
              title = "";

              get links() {
                return [new SidebarChatSearchSectionLink()];
              }

              get text() {
                return null;
              }
            };

            return SidebarChatSearchSection;
          },
          CHAT_PANEL
        );
      }

      api.addSidebarSection(
        (BaseCustomSidebarSection, BaseCustomSidebarSectionLink) => {
          const SidebarChatMyThreadsSectionLink = class extends BaseCustomSidebarSectionLink {
            route = "chat.threads";
            text = i18n("chat.my_threads.title");
            title = i18n("chat.my_threads.title");
            name = "user-threads";
            prefixType = "icon";
            prefixValue = "discourse-threads";
            suffixType = "icon";

            constructor() {
              super(...arguments);

              if (container.isDestroyed) {
                return;
              }
            }

            get suffixValue() {
              return chatChannelsManager.allChannels.some(
                (channel) => channel.unreadThreadsCount > 0
              )
                ? "circle"
                : "";
            }

            get suffixCSSClass() {
              return chatChannelsManager.allChannels.some(
                (channel) => channel.tracking.watchedThreadsUnreadCount > 0
              )
                ? "urgent"
                : "unread";
            }
          };

          const SidebarChatMyThreadsSection = class extends BaseCustomSidebarSection {
            @service chatChannelsManager;

            // we only show `My Threads` link
            hideSectionHeader = true;

            name = "user-threads";

            // sidebar API doesn’t let you have undefined values
            // even if you don't show the section’s header
            title = "";

            get links() {
              return [new SidebarChatMyThreadsSectionLink()];
            }

            get text() {
              return null;
            }

            get displaySection() {
              return this.chatChannelsManager.shouldShowMyThreads;
            }
          };

          return SidebarChatMyThreadsSection;
        },
        CHAT_PANEL
      );

      api.addSidebarSection(
        (BaseCustomSidebarSection, BaseCustomSidebarSectionLink) => {
          const BaseStarredChannelLink = createChannelLink(
            BaseCustomSidebarSectionLink,
            {
              showSuffix: false,
            }
          );

          const SidebarChatStarredChannelLink = class extends BaseStarredChannelLink {
            constructor({ menuService }) {
              super(...arguments);
              this.menuService = menuService;
            }

            get hoverValue() {
              return "ellipsis-vertical";
            }

            get hoverTitle() {
              return i18n("chat.open_channel_menu");
            }

            get hoverAction() {
              return (event, onMenuClose) => {
                event.stopPropagation();
                event.preventDefault();

                this.menuService.show(event.target, {
                  identifier: this.isDM
                    ? "chat-direct-message-channel-menu"
                    : "chat-channel-menu",
                  component: ChatChannelSidebarContextMenu,
                  placement: "right",
                  data: { channel: this.channel },
                  onClose: onMenuClose,
                });
              };
            }
          };

          const SidebarChatStarredChannelsSection = class extends BaseCustomSidebarSection {
            @service currentUser;
            @service chatStateManager;
            @service siteSettings;

            constructor() {
              super(...arguments);

              if (container.isDestroyed) {
                return;
              }
              this.chatService = container.lookup("service:chat");
              this.chatChannelsManager = container.lookup(
                "service:chat-channels-manager"
              );
              this.chatChannelListPreferences = container.lookup(
                "service:chat-channel-list-preferences"
              );
              this.menuService = container.lookup("service:menu");
            }

            get sectionLinks() {
              return this.chatChannelsManager.starredChannelsByPreference.map(
                (channel) =>
                  new SidebarChatStarredChannelLink({
                    channel,
                    chatService: this.chatService,
                    currentUser: this.currentUser,
                    siteSettings: this.siteSettings,
                    chatStateManager: this.chatStateManager,
                    menuService: this.menuService,
                  })
              );
            }

            get name() {
              return CHAT_STARRED_CHANNELS_SECTION;
            }

            get title() {
              return i18n("chat.starred_channels");
            }

            get text() {
              return i18n("chat.starred_channels");
            }

            get links() {
              return this.sectionLinks;
            }

            get displaySection() {
              return (
                this.chatStateManager.hasPreloadedChannels &&
                (this.chatChannelsManager.starredChannelsByPreference.length >
                  0 ||
                  (this.chatChannelsManager.hasStarredChannels &&
                    !this.chatChannelListPreferences.isDefaultFilterFor(
                      "starred"
                    )))
              );
            }

            get actions() {
              return channelListActions(
                "starred",
                this.menuService,
                this.chatChannelListPreferences,
                this.sectionLinks
              );
            }

            get actionsInline() {
              return true;
            }

            get persistentActions() {
              return this.actions.length > 1;
            }

            get actionsIcon() {
              return "ellipsis-vertical";
            }
          };

          return SidebarChatStarredChannelsSection;
        },
        CHAT_PANEL
      );

      if (this.siteSettings.enable_public_channels) {
        api.addSidebarSection(
          (BaseCustomSidebarSection, BaseCustomSidebarSectionLink) => {
            const SidebarChatChannelsSectionLink = class extends BaseCustomSidebarSectionLink {
              hoverType = "icon";
              hoverTitle = i18n("chat.open_channel_menu");

              constructor({
                channel,
                chatService,
                currentUser,
                siteSettings,
                menuService,
              }) {
                super(...arguments);
                this.channel = channel;
                this.chatService = chatService;
                this.currentUser = currentUser;
                this.menuService = menuService;
                this.chatStateManager = chatStateManager;
                this.siteSettings = siteSettings;
              }

              get hoverValue() {
                if (!this.currentUser) {
                  return;
                }

                return "ellipsis-vertical";
              }

              get name() {
                return dasherize(this.channel.slugifiedTitle);
              }

              get classNames() {
                const classes = [];

                if (this.channel.currentUserMembership?.muted) {
                  classes.push("sidebar-section-link--muted");
                }

                if (
                  this.channel.id === this.chatService.activeChannel?.id &&
                  (this.chatStateManager.isDrawerExpanded ||
                    this.chatStateManager.isFullPageActive)
                ) {
                  classes.push("sidebar-section-link--active");
                }

                classes.push(`channel-${this.channel.id}`);

                return classes.join(" ");
              }

              get route() {
                return "chat.channel";
              }

              get models() {
                return this.channel.routeModels;
              }

              get text() {
                return trustHTML(emojiUnescape(this.channel.escapedTitle));
              }

              get prefixType() {
                return displayedEmoji(this.channel) ? "emoji" : "icon";
              }

              get prefixValue() {
                return displayedEmoji(this.channel) ?? "d-chat";
              }

              get prefixColor() {
                return this.channel.chatable.color;
              }

              get title() {
                return this.channel.escapedDescription
                  ? trustHTML(this.channel.escapedDescription)
                  : `${this.channel.escapedTitle} ${i18n("chat.title")}`;
              }

              get prefixBadge() {
                return this.channel.chatable.read_restricted ? "lock" : "";
              }

              get suffixComponent() {
                return ChatSidebarIndicators;
              }

              get suffixArgs() {
                return {
                  unreadCount: this.channel.tracking.unreadCount,
                  // We want to do this so we don't show a blue dot if the user is inside
                  // the channel and a new unread thread comes in.
                  unreadThreadsCount:
                    this.chatService.activeChannel?.id !== this.channel.id
                      ? this.channel.unreadThreadsCountSinceLastViewed
                      : 0,
                  mentionCount: this.channel.tracking.mentionCount,
                  watchedThreadsUnreadCount:
                    this.channel.tracking.watchedThreadsUnreadCount,
                  isDirectMessageChannel: false,
                };
              }

              get hoverAction() {
                if (!this.currentUser) {
                  return noop;
                }

                return (event, onMenuClose) => {
                  event.stopPropagation();
                  event.preventDefault();

                  this.menuService.show(event.target, {
                    identifier: "chat-channel-menu",
                    component: ChatChannelSidebarContextMenu,
                    placement: "right",
                    data: { channel: this.channel },
                    onClose: onMenuClose,
                  });
                };
              }
            };

            const SidebarChatChannelsSection = class extends BaseCustomSidebarSection {
              @service currentUser;
              @service chatStateManager;
              @service siteSettings;

              @tracked
              currentUserCanJoinPublicChannels =
                this.currentUser &&
                (this.currentUser.staff ||
                  this.currentUser.has_joinable_public_channels);

              constructor() {
                super(...arguments);

                if (container.isDestroyed) {
                  return;
                }
                this.chatService = container.lookup("service:chat");
                this.chatChannelsManager = container.lookup(
                  "service:chat-channels-manager"
                );
                this.chatChannelListPreferences = container.lookup(
                  "service:chat-channel-list-preferences"
                );
                this.menuService = container.lookup("service:menu");
              }

              get sectionLinks() {
                return this.chatChannelsManager.sidebarPublicMessageChannels.map(
                  (channel) =>
                    new SidebarChatChannelsSectionLink({
                      channel,
                      chatService: this.chatService,
                      menuService: this.menuService,
                      currentUser: this.currentUser,
                      siteSettings: this.siteSettings,
                    })
                );
              }

              get name() {
                return "chat-channels";
              }

              get title() {
                return i18n("chat.chat_channels");
              }

              get text() {
                return i18n("chat.chat_channels");
              }

              get actions() {
                if (!this.currentUser) {
                  return [];
                }

                return channelListActions(
                  "channels",
                  this.menuService,
                  this.chatChannelListPreferences,
                  this.sectionLinks
                );
              }

              get actionsInline() {
                return true;
              }

              get persistentActions() {
                return this.actions.length > 1;
              }

              get actionsIcon() {
                return "ellipsis-vertical";
              }

              get links() {
                return this.sectionLinks;
              }

              get displaySection() {
                return (
                  this.chatStateManager.hasPreloadedChannels &&
                  (this.chatChannelsManager.unstarredPublicMessageChannels
                    .length > 0 ||
                    this.currentUserCanJoinPublicChannels ||
                    !this.chatChannelListPreferences.isDefaultFilterFor(
                      "channels"
                    ))
                );
              }
            };

            return SidebarChatChannelsSection;
          },
          CHAT_PANEL
        );
      }

      if (this.chatService.userCanDirectMessage) {
        api.addSidebarSection(
          (BaseCustomSidebarSection, BaseCustomSidebarSectionLink) => {
            const SidebarChatNewDirectMessagesSectionLink = class extends BaseCustomSidebarSectionLink {
              route = "chat.new-message";
              name = "new-chat-dm";
              title = i18n("sidebar.start_new_dm.title");
              text = i18n("sidebar.start_new_dm.text");
              prefixType = "icon";
              prefixValue = "plus";
            };

            const SidebarChatDirectMessagesSectionLink = class extends BaseCustomSidebarSectionLink {
              route = "chat.channel";
              suffixType = "icon";
              hoverType = "icon";
              hoverTitle = i18n("chat.open_channel_menu");

              constructor({
                channel,
                chatService,
                currentUser,
                menuService,
                siteSettings,
              }) {
                super(...arguments);
                this.channel = channel;
                this.chatService = chatService;
                this.siteSettings = siteSettings;
                this.currentUser = currentUser;
                this.chatStateManager = chatStateManager;
                this.menuService = menuService;

                if (this.oneOnOneMessage) {
                  const user = this.channel.chatable.users[0];
                  if (user.username !== i18n("chat.deleted_chat_username")) {
                    user.statusManager.trackStatus();
                  }
                }
              }

              @bind
              willDestroy() {
                if (this.oneOnOneMessage) {
                  this.channel.chatable.users[0].statusManager.stopTrackingStatus();
                }
              }

              get hoverValue() {
                return "ellipsis-vertical";
              }

              get oneOnOneMessage() {
                return this.channel.chatable.users.length === 1;
              }

              get suffixComponent() {
                return ChatSidebarIndicators;
              }

              get suffixArgs() {
                return {
                  userStatus: this.oneOnOneMessage
                    ? this.channel.chatable.users[0].get("status")
                    : null,
                  unreadCount: this.channel.tracking.unreadCount,
                  unreadThreadsCount:
                    this.channel.unreadThreadsCountSinceLastViewed,
                  mentionCount: this.channel.tracking.mentionCount,
                  watchedThreadsUnreadCount:
                    this.channel.tracking.watchedThreadsUnreadCount,
                  isDirectMessageChannel: true,
                };
              }

              get name() {
                return this.channel.slugifiedTitle;
              }

              get classNames() {
                const classes = [];

                if (this.channel.currentUserMembership?.muted) {
                  classes.push("sidebar-section-link--muted");
                }

                if (
                  this.channel.id === this.chatService.activeChannel?.id &&
                  (this.chatStateManager.isDrawerExpanded ||
                    this.chatStateManager.isFullPageActive)
                ) {
                  classes.push("sidebar-section-link--active");
                }

                classes.push(`channel-${this.channel.id}`);

                return classes.join(" ");
              }

              get models() {
                return this.channel.routeModels;
              }

              get title() {
                if (this.channel.chatable.group) {
                  return i18n("chat.placeholder_channel", {
                    channelName: this.channel.escapedTitle,
                  });
                } else {
                  return i18n("chat.placeholder_users", {
                    commaSeparatedNames: this.channel.escapedTitle,
                  });
                }
              }

              get text() {
                if (this.channel.chatable.group) {
                  return this.channel.displayTitle;
                } else {
                  const username = this.channel.escapedTitle.replaceAll(
                    "@",
                    ""
                  );
                  return trustHTML(
                    `${escapeExpression(username)}${decorateUsername(
                      escapeExpression(username)
                    )}`
                  );
                }
              }

              get prefixType() {
                if (displayedEmoji(this.channel)) {
                  return "emoji";
                } else if (this.channel.chatable.group) {
                  if (this.channel.chatable.users.length === 1) {
                    return "image";
                  } else {
                    return "text";
                  }
                } else {
                  return "image";
                }
              }

              get prefixValue() {
                if (displayedEmoji(this.channel)) {
                  return displayedEmoji(this.channel);
                } else if (this.channel.chatable.group) {
                  if (this.channel.chatable.users.length === 1) {
                    return avatarUrl(
                      this.channel.chatable.users[0].avatar_template,
                      "tiny"
                    );
                  } else {
                    return this.channel.membershipsCount;
                  }
                } else {
                  return avatarUrl(
                    this.channel.chatable.users[0].avatar_template,
                    "tiny"
                  );
                }
              }

              get prefixCSSClass() {
                const activeUsers = this.chatService.presenceChannel.users;
                const user = this.channel.chatable.users[0];

                if (
                  !!activeUsers?.find((item) => get(item, "id") === user?.id) ||
                  !!activeUsers?.find(
                    (item) => get(item, "username") === user?.username
                  )
                ) {
                  return "active";
                }
                return "";
              }

              get hoverAction() {
                return (event, onMenuClose) => {
                  event.stopPropagation();
                  event.preventDefault();

                  this.menuService.show(event.target, {
                    identifier: "chat-direct-message-channel-menu",
                    component: ChatChannelSidebarContextMenu,
                    placement: "right",
                    data: { channel: this.channel },
                    onClose: onMenuClose,
                  });
                };
              }
            };

            const SidebarChatDirectMessagesSection = class extends BaseCustomSidebarSection {
              @service site;
              @service router;
              @service currentUser;
              @service chatStateManager;
              @service siteSettings;

              constructor() {
                super(...arguments);

                if (container.isDestroyed) {
                  return;
                }

                this.chatService = container.lookup("service:chat");
                this.chatChannelsManager = container.lookup(
                  "service:chat-channels-manager"
                );
                this.chatChannelListPreferences = container.lookup(
                  "service:chat-channel-list-preferences"
                );
                this.menuService = container.lookup("service:menu");
              }

              get hideSectionHeader() {
                return (
                  this.chatChannelsManager.unstarredDirectMessageChannels
                    .length === 0
                );
              }

              get sectionLinks() {
                const channels =
                  this.chatChannelsManager.sidebarDirectMessageChannels;

                if (channels.length > 0) {
                  return channels.map(
                    (channel) =>
                      new SidebarChatDirectMessagesSectionLink({
                        channel,
                        chatService: this.chatService,
                        currentUser: this.currentUser,
                        menuService: this.menuService,
                        siteSettings: this.siteSettings,
                      })
                  );
                } else if (
                  this.chatChannelsManager.unstarredDirectMessageChannels
                    .length === 0
                ) {
                  return [new SidebarChatNewDirectMessagesSectionLink()];
                }

                return [];
              }

              get name() {
                return "chat-dms";
              }

              get title() {
                return i18n("chat.direct_messages.title");
              }

              get text() {
                return i18n("chat.direct_messages.title");
              }

              get actions() {
                return channelListActions(
                  "dms",
                  this.menuService,
                  this.chatChannelListPreferences,
                  this.sectionLinks
                );
              }

              get actionsInline() {
                return true;
              }

              get persistentActions() {
                return this.actions.length > 1;
              }

              get actionsIcon() {
                return "ellipsis-vertical";
              }

              get links() {
                return this.sectionLinks;
              }

              get displaySection() {
                return (
                  this.chatStateManager.hasPreloadedChannels &&
                  (this.chatChannelsManager.sidebarDirectMessageChannels
                    .length > 0 ||
                    this.chatChannelsManager.unstarredDirectMessageChannels
                      .length === 0 ||
                    !this.chatChannelListPreferences.isDefaultFilterFor("dms"))
                );
              }
            };

            return SidebarChatDirectMessagesSection;
          },
          CHAT_PANEL
        );
      }
    });
  },
};
